import type { Universe } from '@/types/universe';
import type { AddRevealRef } from '@/hooks/useScrollReveal';

interface FaqProps {
  universe: Universe;
  openFaq: number | null;
  onToggleFaq: (idx: number) => void;
  addRevealRef: AddRevealRef;
}

export default function Faq({ universe, openFaq, onToggleFaq, addRevealRef }: FaqProps) {
  return (
    <section id="faq" className="bg-background py-24 px-6 md:px-16 lg:px-24">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <p className="flex items-center justify-center text-muted text-[11px] font-semibold uppercase tracking-widest mb-4">
            <span className="accent-line" />
            FAQ
          </p>
          <h2
            ref={addRevealRef}
            className="reveal font-display text-primary"
            style={{ fontSize: 'clamp(32px, 4.5vw, 64px)', fontFamily: "'DM Serif Display', Georgia, serif" }}>
            {universe.faqTitle}{' '}
            <em className="italic text-accent">{universe.faqTitleAccent}</em>
          </h2>
          <p className="text-muted mt-3 text-sm">{universe.faqDesc}</p>
        </div>
        <div className="space-y-3">
          {universe.faqs.map((faq, i) =>
            <div
              key={i}
              ref={addRevealRef}
              className={`reveal reveal-delay-${i % 4 + 1} bg-white border border-primary/10 rounded-2xl overflow-hidden`}>
              <button
                onClick={() => onToggleFaq(i)}
                className="w-full flex items-center justify-between px-6 py-5 text-left">
                <span className="font-semibold text-primary text-sm pr-4">{faq.q}</span>
                <span
                  className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full border border-primary/20 text-primary/60 transition-transform duration-300 ${
                    openFaq === i ? 'rotate-45' : ''
                  }`}>
                  +
                </span>
              </button>
              <div className={`faq-answer ${openFaq === i ? 'open' : ''}`}>
                <p className="px-6 pb-5 text-muted text-sm leading-relaxed">{faq.a}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
